import type { ReactElement } from 'react';
import { useTable, List, ShowButton } from '@refinedev/antd';
import type { HttpError } from '@refinedev/core';
import { Table, Tag, Typography } from 'antd';
import { useGo } from '@refinedev/core';

import type { AdminCheckpointListItem, OrderStatus } from '@/types';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Status color mapping (consistent with OrderList)
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<OrderStatus, string> = {
  INIT: 'default',
  STARTED: 'gold',
  PAYMENT_PENDING: 'gold',
  PAYMENT_COMPLETED: 'blue',
  PAYMENT_FAILED: 'red',
  GENERATING: 'processing',
  GENERATING_PLAN: 'processing',
  GENERATING_CHARACTER_SHEET: 'processing',
  GENERATING_FINAL: 'processing',
  PLAN_REVIEW: 'orange',
  CHARACTER_SHEET_REVIEW: 'orange',
  GENERATION_COMPLETE: 'green',
  GENERATION_FAILED: 'red',
  PREVIEW_READY: 'green',
  CANCELLED: 'default',
  PRINT_JOB_SUBMITTED: 'cyan',
  SHIPPED: 'green',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckpointList(): ReactElement {
  const go = useGo();

  const { tableProps } = useTable<AdminCheckpointListItem, HttpError>({
    resource: 'checkpoints',
    syncWithLocation: true,
  });

  return (
    <List>
      <Table {...tableProps} rowKey="threadId" scroll={{ x: 900 }} size="small">
        <Table.Column<AdminCheckpointListItem>
          dataIndex="threadId"
          title="Thread ID"
          render={(value: string) => (
            <Text copyable style={{ fontSize: 12 }}>
              {value}
            </Text>
          )}
          width={320}
          ellipsis
        />

        <Table.Column<AdminCheckpointListItem>
          dataIndex="orderId"
          title="Order ID"
          render={(value: string | null) =>
            value ? (
              <a
                onClick={(e) => {
                  e.preventDefault();
                  go({
                    to: { resource: 'orders', action: 'show', id: value },
                  });
                }}
                href="#"
                style={{ fontSize: 12 }}
              >
                {value.slice(0, 8)}...
              </a>
            ) : (
              '-'
            )
          }
          width={120}
        />

        <Table.Column<AdminCheckpointListItem>
          dataIndex="orderStatus"
          title="Order Status"
          render={(value: OrderStatus | null) =>
            value ? (
              <Tag color={STATUS_COLORS[value] ?? 'default'}>{value}</Tag>
            ) : (
              '-'
            )
          }
          width={200}
        />

        <Table.Column<AdminCheckpointListItem>
          dataIndex="checkpointCount"
          title="Checkpoints"
          render={(value: number) => value}
          width={110}
          align="center"
        />

        <Table.Column<AdminCheckpointListItem>
          dataIndex="latestCheckpointId"
          title="Latest Checkpoint ID"
          render={(value: string | null) =>
            value ? (
              <Text style={{ fontSize: 12 }}>{value.slice(0, 12)}...</Text>
            ) : (
              '-'
            )
          }
          width={160}
          ellipsis
        />

        <Table.Column<AdminCheckpointListItem>
          title="Actions"
          fixed="right"
          width={80}
          render={(_: unknown, record: AdminCheckpointListItem) => (
            <ShowButton hideText size="small" recordItemId={record.threadId} />
          )}
        />
      </Table>
    </List>
  );
}
